/**
 * PII (개인 식별 정보) 자동 제거 (Phase 1 — 온톨로지 V0).
 *
 * 적용 대상:
 *   1) **path** — query string 의 sensitive key 값 마스킹 (?email=foo@bar → ?email=***)
 *                 + path segment 안의 이메일·전화·카드번호 패턴 마스킹
 *   2) **label** — 클릭 라벨에 동일 패턴 검사
 *   3) **meta** — 객체 deep-walk → string 값 redact
 *
 * 정책:
 *   - 화이트리스트 방식 X (sensitive key 만 명시) — 일반 path 는 그대로 유지
 *   - 패턴 매칭은 server-side 에서 한 번 더 (defense in depth)
 *
 * 누락이 무서운 곳: 검색 쿼리, free-form 입력값, 사용자 입력 form
 */

const PII_PATTERNS: RegExp[] = [
  // 이메일 — RFC 5322 simplified
  /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g,
  // 한국 전화번호 (010-XXXX-XXXX, 010 XXXX XXXX, 01012345678)
  /\b01[0-9][-\s.]?\d{3,4}[-\s.]?\d{4}\b/g,
  // 미국 전화번호 (XXX-XXX-XXXX 또는 +1 XXX XXX XXXX)
  /\b(?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)\d{3}[-.\s]?\d{4}\b/g,
  // 카드번호 — 13~19 자리 (Luhn 검증은 over-engineering · 패턴만)
  /\b(?:\d[ -]?){13,19}\b/g,
  // 한국 주민등록번호 (XXXXXX-XXXXXXX)
  /\b\d{6}[-\s]\d{7}\b/g,
];

/** query string 에서 마스킹할 key 들 (case-insensitive) */
const SENSITIVE_QUERY_KEYS = new Set([
  "email", "e-mail", "mail",
  "phone", "tel", "mobile",
  "password", "passwd", "pwd",
  "token", "access_token", "refresh_token", "id_token",
  "api_key", "apikey", "secret", "client_secret",
  "card", "card_number", "cvc", "cvv",
  "ssn", "rrn",
  "name", "fullname", "username",
  "address", "addr",
]);

/** 문자열 안의 PII 패턴 마스킹 */
function maskPiiPatterns(s: string): string {
  let out = s;
  for (const re of PII_PATTERNS) {
    out = out.replace(re, "***");
  }
  return out;
}

/**
 * URL path 정리 — sensitive query param 마스킹 + PII 패턴 마스킹.
 * 예: "/search?q=foo@bar.com&utm=x" → "/search?q=***&utm=x"
 *     "/users/john@example.com" → "/users/***"
 */
export function redactPath(path: string): string {
  if (!path) return path;
  try {
    // 절대 URL 도 처리
    const isAbsolute = /^https?:\/\//i.test(path);
    const u = isAbsolute ? new URL(path) : new URL(path, "https://x.invalid");
    const params = new URLSearchParams(u.search);
    let changed = false;
    for (const key of Array.from(params.keys())) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        params.set(key, "***");
        changed = true;
      } else {
        const v = params.get(key) || "";
        const masked = maskPiiPatterns(v);
        if (masked !== v) {
          params.set(key, masked);
          changed = true;
        }
      }
    }
    if (changed) u.search = params.toString();
    u.pathname = maskPiiPatterns(u.pathname);
    if (isAbsolute) return u.toString();
    return u.pathname + (u.search ? u.search : "");
  } catch {
    return maskPiiPatterns(path);
  }
}

/** 라벨 (버튼 텍스트 등) 의 PII 마스킹 */
export function redactLabel(label: string): string {
  return label ? maskPiiPatterns(label) : label;
}

/**
 * meta 객체 deep-walk — string 값 PII 마스킹.
 * key 가 sensitive 이면 값 통째로 ***.
 */
export function redactMeta(meta: unknown, depth = 0): unknown {
  if (depth > 4) return "[deep]";
  if (meta == null) return meta;
  if (typeof meta === "string") return maskPiiPatterns(meta);
  if (typeof meta === "number" || typeof meta === "boolean") return meta;
  if (Array.isArray(meta)) return meta.map((v) => redactMeta(v, depth + 1));
  if (typeof meta === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(meta)) {
      if (SENSITIVE_QUERY_KEYS.has(k.toLowerCase())) {
        out[k] = "***";
      } else {
        out[k] = redactMeta(v, depth + 1);
      }
    }
    return out;
  }
  return null;
}
